// services/adminStepUp.ts
//
// Admin passkey step-up (ORA-864): enrolling passkeys, verifying with one, and
// managing the list. A successful ceremony stores the step-up token the axios
// client then sends on every request.
import { isAxiosError } from 'axios';
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import axiosInstance from './axiosCreator';
import {
  AdminStepUpCancelledError,
  storeAdminStepUpToken,
} from './adminStepUpStorage';

export {
  ADMIN_STEP_UP_HEADER,
  ADMIN_STEP_UP_STORAGE_KEY,
  AdminStepUpCancelledError,
  clearAdminStepUpToken,
  getAdminStepUpExpiry,
  getStoredAdminStepUpToken,
  storeAdminStepUpToken,
} from './adminStepUpStorage';

export interface AdminPasskey {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  deviceType: string;
  backedUp: boolean;
}

export interface AdminStepUpStatus {
  enforced: boolean;
  verified: boolean;
  expiresAt: string | null;
}

export interface AdminStepUpGrant {
  token: string;
  expiresAt: string;
}

export interface AdminPasskeyList {
  passkeys: AdminPasskey[];
  stepUp: AdminStepUpStatus;
}

export interface AdminPasskeyRegistration {
  passkey: AdminPasskey;
  stepUp: AdminStepUpGrant;
}

// The passkey ceremony endpoints never answer ADMIN_STEP_UP_REQUIRED (except
// registering a second passkey, which should prompt), and prompting from the
// prompt's own panel would wait on itself. These calls opt out of the prompt.
const NO_STEP_UP_PROMPT = { skipAdminStepUpPrompt: true } as const;

/** Runs a browser passkey ceremony, mapping a user cancel to a typed error. */
const runCeremony = async <T>(ceremony: () => Promise<T>): Promise<T> => {
  try {
    return await ceremony();
  } catch (error) {
    if (isCeremonyCancel(error)) {
      throw new AdminStepUpCancelledError();
    }
    throw error;
  }
};

// Browsers report a dismissed or timed-out passkey sheet as NotAllowedError;
// @simplewebauthn keeps that name on the WebAuthnError it wraps it in.
const isCeremonyCancel = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
    return true;
  }
  return (error as { code?: unknown }).code === 'ERROR_CEREMONY_ABORTED';
};

export const fetchAdminPasskeys = async (): Promise<AdminPasskeyList> => {
  const response = await axiosInstance.get(
    '/admin/passkeys',
    NO_STEP_UP_PROMPT
  );

  return { passkeys: response.data.passkeys, stepUp: response.data.stepUp };
};

export interface RegisterAdminPasskeyOptions {
  /**
   * Skip the axios step-up prompt on these calls. The step-up prompt's own
   * panel sets this: prompting from inside the prompt would wait on itself.
   */
  skipPrompt?: boolean;
}

/**
 * Enrolls a passkey on this device and verifies the admin with it. The first
 * passkey needs no prior step-up; any later one does, so the axios client may
 * prompt before the options call goes through (unless `skipPrompt` is set).
 */
export const registerAdminPasskey = async (
  label?: string,
  { skipPrompt = false }: RegisterAdminPasskeyOptions = {}
): Promise<AdminPasskeyRegistration> => {
  const trimmedLabel = label?.trim() || undefined;
  const requestConfig = skipPrompt ? NO_STEP_UP_PROMPT : undefined;

  const optionsResponse = await axiosInstance.post(
    '/admin/passkeys/register/options',
    { label: trimmedLabel },
    requestConfig
  );

  const { options, challengeToken } = optionsResponse.data as {
    options: PublicKeyCredentialCreationOptionsJSON;
    challengeToken: string;
  };

  const response = await runCeremony(() =>
    startRegistration({ optionsJSON: options })
  );

  const verifyResponse = await axiosInstance.post(
    '/admin/passkeys/register/verify',
    { challengeToken, response, label: trimmedLabel },
    requestConfig
  );

  const { passkey, stepUp } = verifyResponse.data as AdminPasskeyRegistration;

  storeAdminStepUpToken(stepUp.token);
  return { passkey, stepUp };
};

/** Verifies the admin with an enrolled passkey and stores the step-up token. */
export const authenticateWithAdminPasskey =
  async (): Promise<AdminStepUpGrant> => {
    const optionsResponse = await axiosInstance.post(
      '/admin/passkeys/authenticate/options',
      undefined,
      NO_STEP_UP_PROMPT
    );

    const { options, challengeToken } = optionsResponse.data as {
      options: PublicKeyCredentialRequestOptionsJSON;
      challengeToken: string;
    };

    const response = await runCeremony(() =>
      startAuthentication({ optionsJSON: options })
    );

    const verifyResponse = await axiosInstance.post(
      '/admin/passkeys/authenticate/verify',
      { challengeToken, response },
      NO_STEP_UP_PROMPT
    );

    const { stepUp } = verifyResponse.data as { stepUp: AdminStepUpGrant };

    storeAdminStepUpToken(stepUp.token);
    return stepUp;
  };

export const deleteAdminPasskey = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/admin/passkeys/${encodeURIComponent(id)}`);
};

/** The backend's error code on a failed passkey call, if any. */
export const getAdminStepUpErrorCode = (error: unknown): string | null => {
  if (!isAxiosError(error)) return null;
  const code = (error.response?.data as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? code : null;
};

/** A short, admin-facing sentence for a failed passkey action. */
export const describeAdminStepUpError = (error: unknown): string => {
  if (error instanceof AdminStepUpCancelledError) {
    return 'Cancelled. Try again when you are ready.';
  }

  switch (getAdminStepUpErrorCode(error)) {
    case 'PASSKEY_VERIFICATION_FAILED':
      return 'That passkey could not be verified. Try again.';
    case 'ORIGIN_NOT_ALLOWED':
      return 'Passkeys are not available on this web address. Open Ora at its main address and try again.';
    case 'NO_ADMIN_PASSKEY':
      return 'No passkey is set up for this account yet.';
    case 'LAST_ADMIN_PASSKEY':
      return 'You cannot remove your only passkey. Add another one first.';
    case 'ADMIN_STEP_UP_REQUIRED':
      return 'Verify with your passkey first, then try again.';
  }

  if (
    error instanceof Error &&
    (error as { code?: unknown }).code ===
      'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED'
  ) {
    return 'This device already has a passkey for your account.';
  }

  if (isAxiosError(error)) {
    const message = (error.response?.data as { error?: unknown } | undefined)
      ?.error;

    if (typeof message === 'string' && message) return message;
  }

  return 'Something went wrong with your passkey. Try again.';
};
